import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Utensils, Scale, MessageSquare, TrendingUp, Target, Flame, ChevronRight, ChevronDown, ClipboardCheck, CheckCircle2, HelpCircle, Loader2 } from 'lucide-react';
import { LineChart, Line, YAxis, ReferenceLine, ResponsiveContainer, Tooltip } from 'recharts';
import Navbar from '@/components/Navbar';
import FeedbackBanner from '@/components/FeedbackBanner';
import QuickStartGuide from '@/components/QuickStartGuide';
import WhatToDoNowPopup from '@/components/WhatToDoNowPopup';
import { toast } from 'sonner';
import ConsistencyStreak from '@/components/ConsistencyStreak';
import FloatingActionButtons from '@/components/FloatingActionButtons';
import { getBackendURL } from '@/utils/api';

const BACKEND_URL = getBackendURL();
const API = `${BACKEND_URL}/api`;

// FAQ Data
const faqData = [
  {
    question: "How do I use the meal plan?",
    answer: [
      "Pick one option per meal and follow the portions shown.",
      "You can swap meals anytime if something doesn't fit your day."
    ]
  },
  {
  question: "My meal plan calories seems slightly off?",
  answer: [
    "The calories that I've put you on is merely a starting point, not exact data.",
    "Finding out the correct amount of calories requires minimum 2-3 weeks of consistency and daily weigh ins.",
    "It helps me seeing your weekly weight pattern, so i can adjust your calories accordingly",
    "Just follow the plan, and let me handle the calorie adjustments"
  ]
  },
  {
    question: "Do I need to log my weight every day?",
    answer: [
      "Daily weigh-ins are recommended for the most accurate reviews.",
      "Missing a day or two is fine. Progress is based on trends, not single weigh-ins."
    ]
  },
  {
    question: "When does my plan update?",
    answer: [
      "Plans are reviewed weekly.",
      "If adjustments are needed, they're made between Wednesday 00:00 and Thursday 23:59."
    ]
  },
  {
    question: "What if I miss meals or go off plan?",
    answer: [
      "Nothing breaks if it happens occasionally.",
      "Just don't make it a habit. Consistency matters more than perfection."
    ]
  },
  {
    question: "Can I swap recipes or change servings?",
    answer: [
      "You can swap recipes anytime.",
      "Servings help with batch cooking or cooking for others. Portion changes are handled during reviews."
    ]
  },
  {
    question: "Do I have to log every recipe?",
    answer: [
      "No, but logging helps with consistency and awareness.",
      "Weekly reviews work best when meals are logged."
    ]
  },
  {
    question: "Can I add extra food, snacks, or drinks?",
    answer: [
      "Doing anything outside the plan can affect and slow progress. That includes extra food, drinks, fruit, or larger portions.",
      "Having something off plan occasionally is fine. Making it a habit will slow results or reverse them.",
      "Swapping meals outside the plan is meant for planned flexibility. Regularly swapping meals you think are \"about the same\" can still push calories up and affect progress.",
      "Use swaps intentionally, not as a workaround."
    ]
  },
  {
    question: "How do I cancel my subscription?",
    answer: [
      "Go to Profile → Billing to cancel.",
      "You won't be charged again and access ends when your current period finishes."
    ]
  }
];

// FAQ Item Component
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-4 flex items-start justify-between text-left hover:bg-gray-50/50 transition-colors rounded-lg px-2 -mx-2"
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        <ChevronDown 
          className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 pb-4' : 'max-h-0'}`}
      >
        <div className="space-y-2 pl-2">
          {answer.map((line, idx) => (
            <p key={idx} className="text-gray-600 text-sm leading-relaxed">
              {line}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

// FAQ Section Component - Opens in Dialog
function FAQSection() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <>
      {/* FAQ Card - Click to open dialog */}
      <Card 
        className="mt-8 border-gray-200 cursor-pointer hover:shadow-md transition-all hover:border-[#1a61e8]/30"
        onClick={() => setIsOpen(true)}
      >
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-[#1a61e8]/10 rounded-full flex items-center justify-center">
              <HelpCircle className="h-7 w-7 text-[#1a61e8]" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-lg mb-1">FAQ</h3>
              <p className="text-gray-700">Frequently Asked Questions</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </CardContent>
      </Card>

      {/* FAQ Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <HelpCircle className="h-6 w-6 text-[#1a61e8]" />
              Frequently Asked Questions
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-2">
            {faqData.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
            
            {/* Footer link */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setIsOpen(false); navigate('how-it-works'); }}
                className="text-sm text-[#1a61e8] hover:text-[#1552c7] font-medium flex items-center gap-1 transition-colors"
              >
                Check out &quot;How it works&quot; to learn more
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ClientDashboardModern({ user: propUser, onLogout }) {
  const navigate = useNavigate();
  // Local user state — refreshed from /auth/me on mount so trial_days_remaining etc. are always live
  const [user, setUser] = useState(propUser);

  useEffect(() => {
    // Sync user state with backend on dashboard mount (trial countdown, sub status, etc.)
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        if (cancelled) return;
        setUser(res.data);
        try { localStorage.setItem('client_user', JSON.stringify(res.data)); } catch (_) {}
      } catch (_) { /* fall back to prop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const [stats, setStats] = useState({
    mealPlan: null,
    startingWeight: null,
    currentWeight: null,
    goalWeight: null,
    weightHistory: [],
    logStreak: 0,
    loggedRecipes: []
  });
  const [loading, setLoading] = useState(true);
  const [showGoalWeightDialog, setShowGoalWeightDialog] = useState(false);
  const [goalWeightInput, setGoalWeightInput] = useState('');
  const [checkinStatus, setCheckinStatus] = useState({
    isWindowOpen: false,
    completedThisWeek: false,
    showNotification: false
  });
  
  const [weightLoggedToday, setWeightLoggedToday] = useState(false);

  // Quick Start Guide state (menu access only - no auto-show)
  const [showQuickStart, setShowQuickStart] = useState(false);
  
  // What to do now popup state
  const [showWhatToDoPopup, setShowWhatToDoPopup] = useState(false);
  
  // Show "What to do now" popup after orientation (one-time only)
  useEffect(() => {
    const shouldShow = localStorage.getItem('show_what_to_do_popup');
    if (shouldShow === 'true') {
      setShowWhatToDoPopup(true);
      localStorage.removeItem('show_what_to_do_popup'); // Clear flag so it only shows once
    }
  }, []);
  
  const closeWhatToDoPopup = () => {
    setShowWhatToDoPopup(false);
  };
  
  // Feedback state
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchCheckinStatus();
  }, []);

  // Verify Stripe trial-upgrade payment on success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');

    if (payment === 'success' && sessionId) {
      const verifyPayment = async () => {
        try {
          const res = await axios.post(`${API}/payments/verify-trial-upgrade/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            toast.success('Subscription activated! Welcome aboard.', { duration: 4000 });
            // Refresh client_user in localStorage with fresh trial/subscription state
            try {
              const meRes = await axios.get(`${API}/auth/me`);
              if (meRes.data) {
                localStorage.setItem('client_user', JSON.stringify(meRes.data));
              }
            } catch (e) {
              console.warn('Could not refresh user after upgrade:', e);
            }
            // Reload so the trial banner / SubscriptionGuard re-evaluate
            setTimeout(() => {
              window.location.replace('/app');
            }, 1500);
          } else {
            toast.info('Payment is still processing. Please refresh in a moment.', { duration: 5000 });
            window.history.replaceState({}, '', '/app');
          }
        } catch (err) {
          console.error('Payment verification failed:', err);
          toast.error('Could not verify payment. Please contact your coach if the issue persists.');
          window.history.replaceState({}, '', '/app');
        }
      };
      verifyPayment();
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled.');
      window.history.replaceState({}, '', '/app');
    }
  }, []);

  const fetchCheckinStatus = async () => {
    try {
      const res = await axios.get(`${API}/checkin/status`);
      setCheckinStatus({
        isWindowOpen: res.data.is_window_open,
        completedThisWeek: res.data.completed_this_week,
        showNotification: res.data.show_notification
      });
    } catch (err) {
      console.log('Could not fetch check-in status');
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch meal plan
      const mealPlanRes = await axios.get(`${API}/meal-plans/me`);
      
      // Fetch weight logs (non-blocking)
      let weightData = [];
      let startingWeight = null;
      let currentWeight = null;
      let goalWeight = null;
      let loggedToday = false;
      
      try {
        const weightRes = await axios.get(`${API}/weight-logs/my-logs`);
        weightData = weightRes.data || [];
        
        if (weightData.length > 0) {
          // Sort by created_at timestamp (oldest to newest)
          weightData.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          startingWeight = weightData[0].weight;
          currentWeight = weightData[weightData.length - 1].weight;
          goalWeight = weightData[weightData.length - 1].goal_weight;
          
          // Check if weight was logged today
          const today = new Date().toISOString().split('T')[0];
          const lastLogDate = new Date(weightData[weightData.length - 1].created_at).toISOString().split('T')[0];
          loggedToday = today === lastLogDate;
        }
      } catch (weightError) {
        console.log('No weight logs yet');
      }
      
      setWeightLoggedToday(loggedToday);

      // Fetch today's logged recipes
      const loggedRes = await axios.get(`${API}/recipe-logs/today`);
      const loggedRecipes = loggedRes.data || [];
      
      // Calculate recipe logging streak
      const logStreak = await calculateRecipeLogStreak();

      setStats({
        mealPlan: mealPlanRes.data,
        startingWeight,
        currentWeight,
        goalWeight,
        weightHistory: [...weightData]
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .map(w => ({ date: w.date, weight: w.weight })),
        logStreak,
        loggedRecipes
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateRecipeLogStreak = async () => {
    try {
      // Get last 30 days of recipe logs
      const response = await axios.get(`${API}/recipe-logs/streak`);
      return response.data.streak || 0;
    } catch (error) {
      // If endpoint doesn't exist, calculate from recent logs
      return 0;
    }
  };

  const getStreakMessage = () => {
    const streak = stats.logStreak;
    if (streak === 0) return "Start logging meals to build your streak!";
    if (streak === 1) return "1 day of consistency! Keep going! 💪";
    if (streak < 7) return `${streak} days of consistency! You're building a habit! 🔥`;
    if (streak < 14) return `${streak} days strong! You're crushing it! 🚀`;
    if (streak < 30) return `${streak} days! Incredible dedication! ⭐`;
    return `${streak} days! You're unstoppable! 🏆`;
  };

  // Calculate weight change (positive = gained, negative = lost)
  const weightChange = stats.startingWeight && stats.currentWeight 
    ? (stats.currentWeight - stats.startingWeight).toFixed(1)
    : null;

  // Weight trend sparkline: zoom the Y-axis to the actual logged range so day-to-day
  // fluctuation is visible, and only draw the goal reference line when it's close
  // enough to be meaningful (otherwise the "kg to go" stat already covers it).
  const weightValues = stats.weightHistory.map(w => w.weight);
  const chartMin = weightValues.length ? Math.min(...weightValues) : null;
  const chartMax = weightValues.length ? Math.max(...weightValues) : null;
  const chartPadding = chartMin != null ? Math.max((chartMax - chartMin) * 0.4, 1) : 0;
  const chartDomain = chartMin != null ? [chartMin - chartPadding, chartMax + chartPadding] : null;
  const goalInChartRange = chartDomain && stats.goalWeight != null &&
    stats.goalWeight >= chartDomain[0] && stats.goalWeight <= chartDomain[1];
  
  // Feedback handlers
  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(id => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleSubmitFeedback = async () => {
    if (selectedReasons.length === 0 && !feedbackText.trim()) {
      toast.error('Please select at least one reason or provide feedback');
      return;
    }

    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/feedback/submit-authenticated`, {
        reasons: selectedReasons,
        additional_feedback: feedbackText.trim() || null
      });
      
      toast.success('Thank you for your feedback!');
      setShowFeedbackForm(false);
      setSelectedReasons([]);
      setFeedbackText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };
  
  // Format weight change with + or - sign
  const formatWeightChange = (change) => {
    if (!change || change === '0.0') return null;
    const num = parseFloat(change);
    return num > 0 ? `+${change}` : change; // Already has minus sign if negative
  };
  
  const weightToGo = stats.currentWeight && stats.goalWeight
    ? Math.abs(stats.currentWeight - stats.goalWeight).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Skeleton Header */}
          <div className="mb-6">
            <Skeleton className="h-9 w-64 mb-2" />
            <Skeleton className="h-5 w-48" />
          </div>

          {/* Skeleton Hero Card */}
          <Card className="mb-8 overflow-hidden">
            <div className="bg-gradient-to-r from-[#1a61e8] to-[#4a8ff5] p-8">
              <Skeleton className="h-7 w-48 mb-6 bg-white/20" />
              <div className="grid grid-cols-3 gap-6 mb-6">
                <div>
                  <Skeleton className="h-4 w-32 mb-2 bg-white/20" />
                  <Skeleton className="h-9 w-24 bg-white/30" />
                </div>
                <div>
                  <Skeleton className="h-4 w-32 mb-2 bg-white/20" />
                  <Skeleton className="h-9 w-24 bg-white/30" />
                </div>
                <div>
                  <Skeleton className="h-4 w-32 mb-2 bg-white/20" />
                  <Skeleton className="h-9 w-24 bg-white/30" />
                </div>
              </div>
              <Skeleton className="h-3 w-full bg-white/20 rounded-full" />
            </div>
          </Card>

          {/* Skeleton Grid Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <Skeleton className="w-14 h-14 rounded-xl" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-10 w-full rounded-lg" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Skeleton FAQ Card */}
          <Card className="mt-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Skeleton className="w-16 h-16 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-6 w-24 mb-2" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // Trial Expired Screen
  if (user?.is_trial && user?.trial_expired) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-12 border-2 border-orange-200">
            <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Calendar className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Your 7-Day Free Trial Has Ended
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Want to continue your progress and keep working with your coach?
            </p>
            <div className="bg-white rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Get in Touch:</h3>
              <div className="space-y-3">
                <a 
                  href="mailto:support@getmealguide.com"
                  className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                >
                  📧 support@getmealguide.com
                </a>
                <a 
                  href="https://instagram.com/getmealguide"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-pink-600 hover:text-pink-800 font-medium"
                >
                  📱 @getmealguide on Instagram
                </a>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="outline"
              className="mt-4"
            >
              Log Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Feedback Reminder Banner */}
      <FeedbackBanner />
      
      {/* Trial Banner with self-serve Upgrade button (visible from Day 1) */}
      {user?.is_trial && user?.trial_days_remaining !== undefined && !user?.trial_expired && (
        <div className={`py-3 px-4 flex items-center justify-center gap-3 flex-wrap font-medium ${
          user.trial_days_remaining <= 2 
            ? 'bg-orange-500 text-white' 
            : 'bg-yellow-400 text-gray-900'
        }`}>
          <span>⏰ {user.trial_days_remaining} day{user.trial_days_remaining !== 1 ? 's' : ''} left in your free trial</span>
          <button
            data-testid="trial-banner-upgrade-btn"
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/subscription/checkout`);
                if (res.data.checkout_url) {
                  window.location.href = res.data.checkout_url;
                } else {
                  toast.error('No checkout URL returned');
                }
              } catch (err) {
                console.error('Checkout error:', err);
                const errorDetail = err.response?.data?.detail || err.message || 'Unknown error';
                toast.error(`Checkout failed: ${errorDetail}`);
              }
            }}
            className="bg-white hover:bg-gray-50 text-gray-900 text-sm font-semibold px-4 py-1.5 rounded-full shadow-sm transition-colors"
          >
            Upgrade Now — Cancel Anytime
          </button>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user.name || 'there'}! 👋
          </h1>
          <p className="text-gray-600">Here's your daily overview</p>
        </div>

        {/* Floating Action Buttons - positioned above chat bubble */}
        <FloatingActionButtons
          weightLoggedToday={weightLoggedToday}
          checkinDue={checkinStatus.showNotification && !checkinStatus.completedThisWeek}
          checkinCompleted={checkinStatus.completedThisWeek}
        />

        {/* Hero Card - Weight Progress */}
        <Card className="mb-8 overflow-hidden hover-lift">
          <div className="bg-gradient-to-r from-[#1a61e8] to-[#4a8ff5] p-8 text-white">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-4">Your Weight Journey</h2>
              <div className="grid grid-cols-3 gap-6 mb-6">
                <div>
                  <p className="text-blue-100 text-sm mb-1">Starting Weight</p>
                  <p className="text-3xl font-bold">{stats.startingWeight || '—'} kg</p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm mb-1">Current Weight</p>
                  <p className="text-3xl font-bold">{stats.currentWeight || '—'} kg</p>
                  {weightChange && formatWeightChange(weightChange) && (
                    <p className="text-sm text-blue-100 mt-1">{formatWeightChange(weightChange)} kg</p>
                  )}
                </div>
                <div>
                  <p className="text-blue-100 text-sm mb-1">Goal Weight</p>
                  <p className="text-3xl font-bold">{stats.goalWeight || '—'} kg</p>
                  {stats.currentWeight && stats.goalWeight && (
                    <p className="text-sm text-blue-100 mt-1">{weightToGo} kg to go</p>
                  )}
                </div>
              </div>
              
              {/* Weight Trend Sparkline */}
              {stats.goalWeight && stats.weightHistory.length >= 2 ? (
                <div data-testid="weight-trend-sparkline">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-blue-100">Weight Trend</span>
                    {goalInChartRange && (
                      <span className="text-blue-100 flex items-center gap-1">
                        <span className="inline-block w-3 border-t border-dashed border-white/70" />
                        Goal
                      </span>
                    )}
                  </div>
                  <div className="h-20 -mx-2">
                    <ResponsiveContainer width="100%" height="100%" debounce={1}>
                      <LineChart data={stats.weightHistory} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                        <YAxis hide domain={chartDomain} />
                        {goalInChartRange && (
                          <ReferenceLine y={stats.goalWeight} stroke="#ffffff" strokeOpacity={0.6} strokeDasharray="4 4" />
                        )}
                        <Tooltip
                          contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', color: '#111827' }}
                          labelStyle={{ color: '#111827' }}
                          itemStyle={{ color: '#111827' }}
                          labelFormatter={(_, payload) => payload?.[0]?.payload ? new Date(payload[0].payload.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                          formatter={(value) => [`${value} kg`, 'Weight']}
                        />
                        <Line type="monotone" dataKey="weight" stroke="#ffffff" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: '#ffffff' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : stats.goalWeight && stats.weightHistory.length === 1 ? (
                <p data-testid="weight-trend-insufficient-data" className="text-sm text-blue-100">Log your weight a few more times to see your trend line here.</p>
              ) : null}
              
              {/* Set/Edit Goal Weight Button */}
              <button
                data-testid="edit-goal-weight-button"
                onClick={() => {
                  setGoalWeightInput(stats.goalWeight ? String(stats.goalWeight) : '');
                  setShowGoalWeightDialog(true);
                }}
                className="mt-4 bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {stats.goalWeight ? 'Edit Goal Weight' : 'Set Your Goal Weight'}
              </button>
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover-lift transition-all"
            onClick={() => navigate('meal-plan')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-12 h-12 bg-[#1a61e8]/10 rounded-lg flex items-center justify-center mb-3">
                    <Utensils className="h-6 w-6 text-[#1a61e8]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Meal Plan</h3>
                  <p className="text-sm text-gray-600">View & log meals</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover-lift transition-all"
            onClick={() => navigate('weight-log')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-12 h-12 bg-[#1a61e8]/10 rounded-lg flex items-center justify-center mb-3 relative">
                    <Scale className="h-6 w-6 text-[#1a61e8]" />
                    {!weightLoggedToday && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Log Weight</h3>
                  <p className="text-sm text-gray-600">
                    {!weightLoggedToday ? (
                      <span className="text-red-600 font-medium">Log today's weight</span>
                    ) : (
                      'Track your progress'
                    )}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover-lift transition-all"
            onClick={() => navigate('checkin')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3 relative">
                    <MessageSquare className="h-6 w-6 text-purple-600" />
                    {checkinStatus.showNotification && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full border-2 border-white flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Weekly Check-in</h3>
                  <p className="text-sm text-gray-600">
                    {checkinStatus.showNotification ? (
                      <span className="text-orange-600 font-medium">Time for check-in!</span>
                    ) : (
                      'Update your coach'
                    )}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover-lift transition-all"
            onClick={() => navigate('progress')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                    <TrendingUp className="h-6 w-6 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Progress</h3>
                  <p className="text-sm text-gray-600">Measurements & photos</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Week Summary Entry Point */}
        <Card 
          className="mb-8 cursor-pointer hover-lift transition-all border-2 border-[#1a61e8]/20 bg-gradient-to-r from-blue-50 to-indigo-50"
          onClick={() => navigate('week-summary')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#1a61e8] rounded-xl flex items-center justify-center">
                  <span className="text-2xl">📊</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">View This Week's Summary</h3>
                  <p className="text-gray-600">See your progress, feedback, and any plan adjustments</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-[#1a61e8]" />
            </div>
          </CardContent>
        </Card>

        {/* Consistency Streak Section */}
        <div className="mb-8">
          <ConsistencyStreak />
        </div>

        {/* How It Works Button */}
        <Card 
          className="cursor-pointer hover:shadow-md transition-all border-gray-200 hover:border-[#1a61e8]/30"
          onClick={() => navigate('how-it-works')}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#1a61e8]/10 rounded-full flex items-center justify-center">
                <span className="text-2xl">📖</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg mb-1">How this works</h3>
                <p className="text-gray-700">Quick guide to meals, tracking & check-ins</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <FAQSection />

        {/* Feedback Section */}
        <Card id="feedback" className="mt-8 scroll-mt-20">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Share Your Thoughts</h3>
              </div>
              {!showFeedbackForm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFeedbackForm(true)}
                  className="border-blue-200 text-blue-600 hover:bg-blue-50"
                >
                  Give Feedback
                </Button>
              )}
            </div>

            {showFeedbackForm ? (
              <div className="space-y-4 animate-in fade-in duration-300">
                <p className="text-sm text-gray-600">
                  Your feedback helps me improve MealGuide. I read every message personally! - Knut
                </p>

                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    What can I improve? (optional)
                  </Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="dash-feedback-pricing"
                        checked={selectedReasons.includes('too_expensive')}
                        onCheckedChange={() => handleReasonToggle('too_expensive')}
                      />
                      <label htmlFor="dash-feedback-pricing" className="text-sm cursor-pointer">
                        Pricing
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="dash-feedback-features"
                        checked={selectedReasons.includes('missing_features')}
                        onCheckedChange={() => handleReasonToggle('missing_features')}
                      />
                      <label htmlFor="dash-feedback-features" className="text-sm cursor-pointer">
                        Missing features
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="dash-feedback-ux"
                        checked={selectedReasons.includes('not_using')}
                        onCheckedChange={() => handleReasonToggle('not_using')}
                      />
                      <label htmlFor="dash-feedback-ux" className="text-sm cursor-pointer">
                        User experience
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="dash-feedback-tech"
                        checked={selectedReasons.includes('technical_issues')}
                        onCheckedChange={() => handleReasonToggle('technical_issues')}
                      />
                      <label htmlFor="dash-feedback-tech" className="text-sm cursor-pointer">
                        Technical issues
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="dash-feedback-other"
                        checked={selectedReasons.includes('other')}
                        onCheckedChange={() => handleReasonToggle('other')}
                      />
                      <label htmlFor="dash-feedback-other" className="text-sm cursor-pointer">
                        Other
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="dash-feedback-text" className="text-sm font-medium mb-2 block">
                    Your thoughts
                  </Label>
                  <Input
                    id="dash-feedback-text"
                    placeholder="Share your ideas, suggestions, or concerns..."
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmitFeedback}
                    disabled={submittingFeedback}
                    className="flex-1 bg-[#3477f8] hover:bg-[#2563eb]"
                  >
                    {submittingFeedback ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
                    ) : (
                      'Send to Knut'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowFeedbackForm(false);
                      setSelectedReasons([]);
                      setFeedbackText('');
                    }}
                    disabled={submittingFeedback}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Have ideas or feedback? I'd love to hear from you.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Goal Weight Dialog */}
      <Dialog open={showGoalWeightDialog} onOpenChange={setShowGoalWeightDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Your Goal Weight</DialogTitle>
            <DialogDescription>
              Enter your target weight to track your progress
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="goalWeight">Goal Weight (kg)</Label>
              <Input
                id="goalWeight"
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                placeholder="e.g. 75.0 or 75,0"
                value={goalWeightInput}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.,]/g, '');
                  const firstSep = v.search(/[.,]/);
                  const cleaned = firstSep === -1
                    ? v
                    : v.slice(0, firstSep + 1) + v.slice(firstSep + 1).replace(/[.,]/g, '');
                  setGoalWeightInput(cleaned);
                }}
                className="mt-2"
              />
            </div>
            <Button 
              onClick={async () => {
                const normalized = (goalWeightInput || '').toString().replace(',', '.').trim();
                const parsed = parseFloat(normalized);
                if (!normalized || isNaN(parsed) || parsed <= 0) {
                  toast.error('Please enter a valid weight');
                  return;
                }
                
                try {
                  await axios.put(`${API}/weight-logs/goal`, { goal_weight: parsed });
                  toast.success('Goal weight set successfully!');
                  setShowGoalWeightDialog(false);
                  setGoalWeightInput('');
                  fetchDashboardData(); // Refresh data
                } catch (error) {
                  console.error('Error setting goal weight:', error);
                  toast.error('Failed to set goal weight');
                }
              }}
              className="w-full bg-[#1a61e8] hover:bg-[#1552c7]"
            >
              Save Goal Weight
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Start Guide Modal */}
      <QuickStartGuide
        isOpen={showQuickStart}
        onClose={() => setShowQuickStart(false)}
        autoShow={false}
        userEmail={user?.email || user?.user_email || user?.id}
      />
      
      {/* What to do now popup - shows once after orientation */}
      <WhatToDoNowPopup 
        open={showWhatToDoPopup} 
        onClose={closeWhatToDoPopup}
      />
    </div>
  );
}
